import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ILiveGameConfig } from '@core/js/hooks/games/types';

import { clsx } from '@interlayer/js/utils/classNames.util';

import BaccaratTable from './BaccaratTable';
import { BLACK_NUMBERS, LiveDataTypes, MAX_ROULLETE_RESULTS, RED_NUMBERS } from './constants';

import style from './LiveDataContainer.scss';

interface IGameConfigProp extends ILiveGameConfig {
    results: string[];
    playerCount: string;
}

type LiveStreamingVideoProps = {
    liveGameConfig: IGameConfigProp;
    liveType: string | undefined;
    sizes: { width: number; height: number };
    results: string[];
    playerCount: string;
}

type RouletteLiveDataResultsProps = {
    result: string;
    id: number;
}

const RouletteLiveDataResults = memo(({ result, id }: RouletteLiveDataResultsProps) => {
    const getRouletteNumberColor = useCallback((rouletteNumber: number): string => {
        if (RED_NUMBERS.includes(rouletteNumber)) { return style.redRouletteNumber; }

        if (BLACK_NUMBERS.includes(rouletteNumber)) { return style.blackRouletteNumber; }
        return style.zeroRouletteNumber;
    }, []);

    const {
        resultNumber: memoResultNumber,
        colorClass: memoColorClass,
        firstItemClass: memoFirstItemClass
    } = useMemo(() => {
        const parsedResultNumber = parseInt(result.split(';')[0]);
        const colorClass = getRouletteNumberColor(parsedResultNumber);
        const firstItemClass = id === 0 ? style.firstRouletteResultItem : '';
        return { resultNumber: parsedResultNumber, colorClass, firstItemClass };
    }, [result, id, getRouletteNumberColor]);

    return (
        <span
            className={clsx([memoColorClass, memoFirstItemClass, style.rouletteResultsItem])}
        >
            {memoResultNumber}
        </span>
    );
});

const LiveDataContainer = ({ liveGameConfig, liveType, sizes }: LiveStreamingVideoProps) => {
    const { results } = liveGameConfig;

    const [rouletteResults, setRouletteResults] = useState<string[]>([]);

    useEffect(() => {
        if (results) {
            setRouletteResults((prevResults: string[]): string[] => {
                const updatedResults = [...results, ...prevResults];
                const newResults = updatedResults.slice(0, MAX_ROULLETE_RESULTS);
                return newResults;
            });
        }
    }, [results]);

    return (
        <div className={clsx(style.liveDataContainer,
            { [style.liveDataRouletteContainer]: rouletteResults && liveType === LiveDataTypes.ROULETTE },
            { [style.slimContainer]: sizes?.width === 1 })}
        >
            {liveType === LiveDataTypes.BACCARAT && <BaccaratTable data={results} />}

            {rouletteResults && liveType === LiveDataTypes.ROULETTE && (
                <div className={style.rouletteResults}>
                    {rouletteResults.map((result, id) => <RouletteLiveDataResults result={result} key={id} id={id} />)}
                </div>
            )}
        </div>
    );
};

export default LiveDataContainer;
